import type {
  AuthenticatedUser,
  IResource,
  Office,
  ResourceType
} from "@gettactic/api";

const ORG_SLUG_REQUIRES_RESERVATION_AFTER_PICKING_WL = Object.freeze([
  "tesla",
  "staging",
  "grammarly",
  "demo"
]);

// Redirects to health questionnaire / survey immediately after creating a reservation
const ORG_SLUG_RESERVATION_SURVEY = Object.freeze([
  "deighton",
  "pictonmahoney"
]);

/**
 * Organizations whose users carry HR details synced from SailPoint. Besides
 * Bridgestone itself these are the two environments the feature is tested on:
 * `gondor` (develop) and `tactic` (the main domain).
 */
const ORG_SLUG_BRIDGESTONE = Object.freeze(["bridgestone", "gondor", "tactic"]);

const ORG_SLUG_PRIVACY_REPORTS = Object.freeze(["altair"]);

const ORG_SLUG_NO_VISITOR_REPORTS = Object.freeze(["altair", "hpe"]);

const ORG_SLUG_RB_FEATURES = Object.freeze([
  "redbull",
  "staging",
  "chatarra",
  "alekhya"
]);

const ORG_SLUG_MAP_BUILDER_EDITOR_ENABLED = Object.freeze([
  "demo",
  "tactic",
  "staging",
  "gondor",
  "softserve"
]);

/**
 * An array of user IDs that will force the recording of sessions in Sentry Session Replay.
 * */
const FORCE_SESSION_REPLAY_USERS: readonly string[] = Object.freeze([]);

const ORG_USER_DASH_BETA = Object.freeze([
  "user_4180c132-b8c2-4a31-89b0-b69d6ff19e15",
  "user_1dd31aae-68ad-4e27-8778-5378b861417e"
]);

export function isWorkingLocationsWithOOOFeaturesEnabled(
  user?: AuthenticatedUser
) {
  return user?.organization?.features.includes("leave.management");
}

export function isReservationSurveyEnabled(user?: AuthenticatedUser) {
  return (
    user && ORG_SLUG_RESERVATION_SURVEY.includes(user?.organization?.slug ?? "")
  );
}

export function canUseMapBuilderEditor(user?: AuthenticatedUser) {
  return (
    user &&
    ORG_SLUG_MAP_BUILDER_EDITOR_ENABLED.includes(user?.organization?.slug ?? "")
  );
}

/** Users who see the SVG map editor (`@gettactic/map-editor`) instead of
 *  the Konva one, and the map page's SVG viewer instead of the current one.
 *  Empty while the rebuild is experimental; a developer can opt in per
 *  browser with `localStorage.mapBuilder = "2"`. */
const SVG_MAP_EDITOR_USER_IDS: readonly string[] = Object.freeze([]);

export function hasEnabledSvgMapEditor(user?: AuthenticatedUser) {
  if (user && SVG_MAP_EDITOR_USER_IDS.includes(user.user?.id ?? "")) {
    return true;
  }
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem("mapBuilder") === "2";
  } catch {
    return false;
  }
}

export function isPrivacyReportsEnabled(user?: AuthenticatedUser) {
  return (
    user && ORG_SLUG_PRIVACY_REPORTS.includes(user?.organization?.slug ?? "")
  );
}

export function isNotAllowedToSelectAllOfficesForReports(
  user?: AuthenticatedUser
) {
  return user && ["hpe"].includes(user?.organization?.slug ?? "");
}

export function isVisitorReportDisabled(user?: AuthenticatedUser) {
  return (
    user && ORG_SLUG_NO_VISITOR_REPORTS.includes(user?.organization?.slug ?? "")
  );
}

export function hasEnabledDeleteResource(
  user?: AuthenticatedUser,
  resource?: IResource | null
) {
  return isTacticStaffOrImpersonated(user) || resource?.type === "area";
}

export function isSingleUserDashBeta(user?: AuthenticatedUser) {
  return user && ORG_USER_DASH_BETA.includes(user?.user?.id ?? "");
}

export function isRBFeature(user?: AuthenticatedUser) {
  return user && ORG_SLUG_RB_FEATURES.includes(user?.organization?.slug ?? "");
}

export function isSessionReplayForcedForUser(user?: AuthenticatedUser) {
  if (!user) {
    return;
  }
  if (user?.user?.is_being_impersonated) {
    return;
  }
  return FORCE_SESSION_REPLAY_USERS.includes(user?.user?.id ?? "");
}

const NO_USER_INTEGRATION_ORGS = Object.freeze({
  "orgz_e92857ae-c10b-48a0-8e47-5608a9b60a5c": "veeam"
});

const ORG_SLUG_NO_SCHEDULES = Object.freeze(["redbull"]);

const SKIP_MAP_CLICK_ORG = Object.freeze(["instructure"]);

export function isHealthModuleRestrictionsEnabled(
  user?: AuthenticatedUser,
  office?: Office
) {
  return user && !!office?.health_setting;
}

export function isHealthModuleEnabled(user?: AuthenticatedUser) {
  return !!user && !!user?.organization?.health_module_enabled;
}

export function isHealthAdmin(user?: AuthenticatedUser) {
  return !!user?.user?.organization_user.health_is_manager;
}

/**
 * Shows a custom message for the currentOffice, not the default office
 */
export function getCustomOfficeDashboardMessage(officeId: string) {
  return officeId in CUSTOM_OFFICE_BANNER_MESSAGES
    ? CUSTOM_OFFICE_BANNER_MESSAGES[
        officeId as keyof typeof CUSTOM_OFFICE_BANNER_MESSAGES
      ]
    : "";
}

const CUSTOM_OFFICE_BANNER_MESSAGES = {
  "offi_7e9bad16-c43f-4cc8-bf5c-ccc670ba2283":
    "Reminder to close all doors and shut off all lights", // 1010Data, NYC
  "offi_329d0b4a-9d68-42fa-9d4f-b11770ba4106":
    "Reminder to close all doors and shut off all lights", // Gondor, Main Office 3
  "offi_2796ada9-a851-4b8a-aabc-caaa04887eea":
    "Please don't forget to bring a mouse and laptop charger!", // Nitro Toronto
  "offi_cea10108-9e5f-4267-b4aa-b938d46af296":
    "Please don't forget to bring a mouse and laptop charger!" // Nitro Dublin
};

export function isTacticStaff(user?: AuthenticatedUser) {
  return (
    user?.user?.email &&
    (user.user.email.endsWith("@gettactic.com") ||
      user.user.email.endsWith("@tactic-staging.com") ||
      user.user.email.endsWith("@gettactic.onmicrosoft.com") ||
      process.env.NODE_ENV === "development")
  );
}

export function isTacticStaffOrImpersonated(user?: AuthenticatedUser) {
  return isTacticStaff(user) || user?.user?.is_being_impersonated;
}

export function isVisitorManager(user?: AuthenticatedUser) {
  return (
    (user?.user?.organization_user?.roles?.includes("visitor_manager") &&
      !user?.permissions?.settings) ??
    false
  );
}

export function isLabelFieldEnabled(user?: AuthenticatedUser) {
  return isTacticStaffOrImpersonated(user);
}

export function showRecurring(
  resourceType: ResourceType,
  authenticatedUser: AuthenticatedUser,
  office: Office
) {
  const canScheduleRecurring =
    authenticatedUser.user?.organization_user?.can_schedule_recurring;
  const canScheduleRecurringEnabled =
    authenticatedUser.user?.organization_user?.can_schedule_recurring_enabled;

  // Admin only
  if (canScheduleRecurring) {
    return true;
  }

  // If not an admin, check office + resource type combination
  if (canScheduleRecurringEnabled) {
    if (!office.allow_recurring) {
      return false;
    }

    // Either allow_recurring_types is null, an empty array, or contains the resource type
    if (
      !office.allow_recurring_types ||
      (office.allow_recurring_types &&
        (office.allow_recurring_types.length === 0 ||
          office.allow_recurring_types.includes(resourceType)))
    ) {
      return true;
    }
  }

  return false;
}

export function isWorkingLocationsEnabled(user?: AuthenticatedUser) {
  return (
    isTacticStaff(user) ||
    user?.organization?.features.includes("dashboard.new") ||
    isSingleUserDashBeta(user)
  );
}

/**
 * Whether to surface the employment details synced from SailPoint (employee
 * number, cost center, manager, employee type, hire date). Gated per
 * organization because only Bridgestone syncs them today.
 */
export function isBridgestone(user?: AuthenticatedUser) {
  return ORG_SLUG_BRIDGESTONE.includes(user?.organization?.slug ?? "");
}

/**
 * Whether the viewer may see the employment details. Restricted to admins on
 * top of the organization gate: the synced payroll data (employee number, cost
 * center, reporting line) is not something every colleague should read off each
 * other's profile.
 */
export function canViewEmploymentDetails(user?: AuthenticatedUser) {
  return isBridgestone(user) && Boolean(user?.permissions?.settings);
}

export function orgRequiresReservationAfterPickingAnOffice(
  user?: AuthenticatedUser
) {
  return ORG_SLUG_REQUIRES_RESERVATION_AFTER_PICKING_WL.includes(
    user?.organization?.slug ?? ""
  );
}

export function isSchedulesDisabled(user?: AuthenticatedUser) {
  return user && ORG_SLUG_NO_SCHEDULES.includes(user?.organization?.slug ?? "");
}

export function isTemporarySkipMapClickOrg(user?: AuthenticatedUser) {
  return user && SKIP_MAP_CLICK_ORG.includes(user?.organization?.slug ?? "");
}

export function isPersonalIntegrationsDisabled(user?: AuthenticatedUser) {
  return (
    user?.organization !== null &&
    NO_USER_INTEGRATION_ORG_IDS.find((x) => x === user?.organization?.id)
  );
}

/**
 * ID's exported to separate pages
 */

export const NO_USER_INTEGRATION_ORG_IDS = Object.freeze(
  Object.keys(NO_USER_INTEGRATION_ORGS)
);

/**
 * Hide restriction / permissions UI for zones until API is ready.
 * See https://github.com/gettactic/api/issues/69
 */

export const isWorkplaceRequestsEnabled = (
  featureFlagEnabled?: boolean | null
) => !!featureFlagEnabled;

export const isVisitorManagementEnabled = (
  featureFlagEnabled?: boolean | null
) => !!featureFlagEnabled;

export const isRoomDisplaysEnabled = (featureFlagEnabled?: boolean | null) =>
  !!featureFlagEnabled;

export const isWayfinderDisplaysEnabled = (
  featureFlagEnabled?: boolean | null
) => !!featureFlagEnabled;

/**
 * Frontend kill switch for the post-reservation visitor pre-registration prompt.
 * Set to `true` to enable the feature.
 */
export const IS_POST_RESERVATION_VISITOR_PROMPT_ENABLED = true;
